import React, { useState, useEffect, useRef } from 'react';
import ProcurementDocuments from '../../Common/ProcurementDocuments';
import { toast } from 'react-hot-toast';
import PaymentFormHeader from './PaymentFormHeader';
import GRNAllocationGrid from './GRNAllocationGrid';
import SRNAllocationGrid from './SRNAllocationGrid';
import DebitNoteAllocationGrid from './DebitNoteAllocationGrid';
import AdvanceAllocationGrid from './AdvanceAllocationGrid';
import PaymentSummarySection from './PaymentSummarySection';
import PaymentModesGrid from './PaymentModesGrid';
import LoadingSpinner from '../../Common/LoadingSpinner';
import inventoryApi from '../../../services/inventoryApi';

const SupplierPaymentForm = ({ paymentId, onClose, onSuccess }) => {
  const id = paymentId;
  const isEdit = Boolean(id);
  const docsRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    paymentVoucherNo: '',
    paymentDate: new Date().toISOString().split('T')[0],
    costCenter: '',
    supplier: '',
    supplierCode: '',
    supplierLabel: '',
    paymentType: 'against_grn',
    paymentMethod: 'multiple',
    bankAccount: '',
    accountNo: '',
    status: 'pending',
    chequeDetails: {
      chequeNo: '',
      chequeDate: '',
      bankName: ''
    },
    transferDetails: {
      referenceNo: '',
      transferDate: ''
    },
    notes: ''
  });

  const [grnAllocations, setGRNAllocations] = useState([]);
  const [srnAllocations, setSRNAllocations] = useState([]);
  const [debitNoteAllocations, setDebitNoteAllocations] = useState([]);
  const [advanceAllocations, setAdvanceAllocations] = useState([]);
  // Advance / On Account: the amount paid without GRNs (+ the PO an advance is for)
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [advancePurchaseOrder, setAdvancePurchaseOrder] = useState('');
  const [supplierPOs, setSupplierPOs] = useState([]);
  const [creditNotesApplied, setCreditNotesApplied] = useState(0);
  const [creditNote, setCreditNote] = useState({ accountId: '', label: '', reference: '' });
  const [paymentModes, setPaymentModes] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const isAdvanceType = ['advance', 'on_account'].includes(formData.paymentType);

  // Load form data on edit
  useEffect(() => {
    if (isEdit) loadPaymentData();
  }, [id]);

  // An advance can be for one of the supplier's purchase orders
  useEffect(() => {
    if (formData.paymentType !== 'advance' || !formData.supplier) { setSupplierPOs([]); return; }
    inventoryApi.getPurchaseOrders({ supplier: formData.supplier, limit: 100 })
      .then(res => {
        const d = res.data;
        const list = Array.isArray(d) ? d : d?.purchaseOrders || d?.orders || [];
        setSupplierPOs(list.filter(po => !['draft', 'cancelled', 'rejected'].includes(po.status)));
      })
      .catch(() => setSupplierPOs([]));
  }, [formData.paymentType, formData.supplier]);

  const handleSupplierChange = (supplierId, label, supplierObj) => {
    setFormData(prev => ({
      ...prev,
      supplier: supplierId || '',
      supplierLabel: label || '',
      supplierCode: supplierObj?.code || ''
    }));
    if (errors.supplier) setErrors(prev => ({ ...prev, supplier: null }));
  };

  const loadPaymentData = async () => {
    try {
      setLoading(true);
      const response = await inventoryApi.getSupplierPayment(id);
      const payment = response.data;

      setFormData({
        paymentVoucherNo: payment.paymentVoucherNo || '',
        paymentDate: payment.paymentDate ? new Date(payment.paymentDate).toISOString().split('T')[0] : '',
        costCenter: payment.costCenter?._id || '',
        supplier: payment.supplier?._id || '',
        supplierCode: payment.supplierCode || payment.supplier?.code || '',
        supplierLabel: payment.supplier?.code && payment.supplier?.name
          ? `${payment.supplier.code} - ${payment.supplier.name}`
          : payment.supplier?.name || '',
        paymentType: payment.paymentType || 'against_grn',
        paymentMethod: payment.paymentMethod || 'bank_transfer',
        bankAccount: payment.bankAccount?._id || '',
        accountNo: payment.accountNo || '',
        status: payment.status || 'draft',
        chequeDetails: payment.chequeDetails || { chequeNo: '', chequeDate: '', bankName: '' },
        transferDetails: payment.transferDetails || { referenceNo: '', transferDate: '' },
        notes: payment.notes || ''
      });

      setGRNAllocations(payment.grnAllocations || []);
      setSRNAllocations(payment.srnAllocations || []);
      setDebitNoteAllocations(payment.debitNoteAllocations || []);
      setAdvanceAllocations(payment.advanceAllocations || []);
      setAdvanceAmount(payment.advance?.amount || 0);
      setAdvancePurchaseOrder(String(payment.advance?.purchaseOrder?._id || payment.advance?.purchaseOrder || ''));
      setCreditNotesApplied(payment.summary?.creditNotesApplied || 0);
      setCreditNote({
        accountId: String(payment.creditNote?.ledgerAccount || ''),
        label: payment.creditNote?.ledgerAccountLabel || '',
        reference: payment.creditNote?.reference || ''
      });
      setPaymentModes((payment.paymentModes || []).map(m => ({
        ...m,
        ledgerAccount: m.ledgerAccount || '',
        ledgerAccountLabel: m.ledgerAccountLabel || ''
      })));
    } catch (error) {
      console.error('Error loading payment data:', error);
      toast.error('Failed to load payment data');
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableGRNs = async (supplierId) => {
    try {
      const response = await inventoryApi.getAvailableGRNsForPayment(supplierId);
      return response.data || [];
    } catch (error) {
      console.error('Error loading available GRNs:', error);
      toast.error('Failed to load available GRNs');
      return [];
    }
  };

  const loadAvailableSRNs = async (supplierId) => {
    try {
      const response = await inventoryApi.getReturnNotesForSetoff(supplierId);
      return response.data || [];
    } catch (error) {
      console.error('Error loading available SRNs:', error);
      toast.error('Failed to load available supplier return notes');
      return [];
    }
  };

  const loadAvailableDebitNotes = async (supplierId) => {
    try {
      const response = await inventoryApi.getDebitNotesForSetoff(supplierId);
      return response.data || [];
    } catch (error) {
      console.error('Error loading available debit notes:', error);
      toast.error('Failed to load available debit notes');
      return [];
    }
  };

  const loadAvailableAdvances = async (supplierId) => {
    try {
      const response = await inventoryApi.getSupplierAdvancesForSetoff(supplierId);
      return response.data || [];
    } catch (error) {
      console.error('Error loading supplier advances:', error);
      toast.error('Failed to load open advances');
      return [];
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Advance / On Account payments carry no GRNs or set-offs (they are set off later)
    if (name === 'paymentType' && ['advance', 'on_account'].includes(value)) {
      setGRNAllocations([]);
      setSRNAllocations([]);
      setDebitNoteAllocations([]);
      setAdvanceAllocations([]);
    }

    // Handle nested object properties
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear related errors
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.supplier) newErrors.supplier = 'Supplier is required';
    // if (!formData.costCenter) newErrors.costCenter = 'Cost center is required';
    if (!formData.paymentDate) newErrors.paymentDate = 'Payment date is required';
    if (!formData.paymentType) newErrors.paymentType = 'Payment type is required';
    if (!formData.paymentMethod) newErrors.paymentMethod = 'Payment method is required';

    if (!isAdvanceType && creditNotesApplied > 0 && !creditNote.accountId) {
      newErrors.creditNote = 'Choose the account the credit note is credited to';
    }
    if (creditNotesApplied < 0) newErrors.creditNote = 'Credit notes applied cannot be negative';
    if (isAdvanceType && !(advanceAmount > 0)) {
      newErrors.advanceAmount = `Enter the amount paid ${formData.paymentType === 'advance' ? 'in advance' : 'on account'}`;
    }
    if (!isAdvanceType && advanceAllocations.some(a => (a.amountSetoff || 0) - (a.setoffAmount || 0) > 0.001)) {
      newErrors.advanceAllocations = 'An advance set-off is more than what is still open on it';
    }

    // GRN allocations validation
    if (formData.paymentType === 'against_grn') {
      if (grnAllocations.length === 0) {
        newErrors.grnAllocations = 'At least one GRN allocation is required';
      } else {
        const hasInvalidAmounts = grnAllocations.some(allocation =>
          !allocation.amountPaid || allocation.amountPaid <= 0 || allocation.amountPaid > allocation.outstandingBalance
        );
        if (hasInvalidAmounts) {
          newErrors.grnAllocations = 'All GRN allocations must have valid payment amounts';
        }
      }
    }

    // Payment modes validation (none needed when set-offs settle everything)
    if (paymentModes.length === 0) {
      if (netPaymentAmount > 0.005) newErrors.paymentModes = 'At least one payment mode is required';
      else if (netPaymentAmount < -0.005) newErrors.paymentModes = `The set-offs are ${(-netPaymentAmount).toFixed(2)} more than the amount being paid`;
    } else {
      const totalPaymentModesAmount = paymentModes.reduce((sum, mode) => sum + (mode.amount || 0), 0);
      if (Math.abs(totalPaymentModesAmount - netPaymentAmount) > 0.005) {
        newErrors.paymentModes = `Total payment modes amount (${totalPaymentModesAmount.toFixed(2)}) must equal net payment amount (${netPaymentAmount.toFixed(2)})`;
      }

      // Validate individual payment modes
      const hasInvalidModes = paymentModes.some(mode => {
        if (!mode.paymentMethod || !mode.amount || mode.amount <= 0) return true;
        if (!mode.ledgerAccount) return true;
        if (mode.paymentMethod === 'cheque' && (!mode.chequeDetails?.chequeNo || !mode.chequeDetails?.chequeDate)) return true;
        if (mode.paymentMethod === 'bank_transfer' && !mode.transferDetails?.referenceNo) return true;
        return false;
      });

      if (hasInvalidModes) {
        newErrors.paymentModes = 'All payment modes must have valid details';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Net paid out now: GRNs (or the advance amount) − credit note − set-offs
  const sumOf = (list, key) => list.reduce((sum, a) => sum + (a[key] || 0), 0);
  const netPaymentAmount = Math.round((sumOf(grnAllocations, 'amountPaid') + (isAdvanceType ? (parseFloat(advanceAmount) || 0) : 0)
    - (isAdvanceType ? 0 : creditNotesApplied) - sumOf(srnAllocations, 'amountSetoff') - sumOf(debitNoteAllocations, 'amountSetoff')
    - (isAdvanceType ? 0 : sumOf(advanceAllocations, 'amountSetoff'))) * 100) / 100;

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    try {
      setSaving(true);

      const paymentData = {
        ...formData,
        grnAllocations: isAdvanceType ? [] : grnAllocations,
        srnAllocations: isAdvanceType ? [] : srnAllocations,
        debitNoteAllocations: isAdvanceType ? [] : debitNoteAllocations,
        advanceAllocations: isAdvanceType ? [] : advanceAllocations,
        advanceAmount: isAdvanceType ? (parseFloat(advanceAmount) || 0) : 0,
        advancePurchaseOrder: formData.paymentType === 'advance' && advancePurchaseOrder ? advancePurchaseOrder : null,
        creditNotesApplied: isAdvanceType ? 0 : creditNotesApplied,
        ...(!isAdvanceType && creditNotesApplied > 0 && {
          creditNoteAccount: { accountId: creditNote.accountId, label: creditNote.label },
          creditNoteReference: creditNote.reference
        }),
        paymentModes
      };

      let response;
      if (isEdit) {
        response = await inventoryApi.updateSupplierPayment(id, paymentData);
      } else {
        response = await inventoryApi.createSupplierPayment(paymentData);
        await docsRef.current?.uploadPending(response?.data?._id);
      }

      toast.success('Payment saved successfully and ready for approval');
      onSuccess?.(response?.data);
    } catch (error) {
      console.error('Error saving payment:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save payment';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose?.();
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-white shadow-2xl border-0 flex flex-col rounded-2xl overflow-hidden">
      {loading && <LoadingSpinner />}
      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 border-b border-gray-200 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">
                {isEdit ? 'Edit Payment' : 'New Payment'}
              </h3>
              <p className="text-indigo-100 text-sm mt-1">
                {formData.paymentVoucherNo || 'Create and manage supplier payments'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${formData.status === 'draft' ? 'bg-gray-100 text-gray-800' :
              formData.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                formData.status === 'approved' ? 'bg-green-100 text-green-800' :
                  formData.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                    'bg-red-100 text-red-800'
              }`}>
              {formData.status?.toUpperCase()}
            </span>
            <button
              onClick={handleCancel}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-2">
        <div className="space-y-3">
          {/* Payment Header Section */}
          <PaymentFormHeader
            formData={formData}
            onChange={handleInputChange}
            onSupplierChange={handleSupplierChange}
            supplierLabel={formData.supplierLabel}
            errors={errors}
            loading={saving}
          />

          {/* Advance / On Account: the amount paid now, set off later in an Against GRN payment */}
          {isAdvanceType && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-teal-600 to-emerald-600 px-3 py-1.5">
                <span className="text-xs font-semibold text-white">{formData.paymentType === 'advance' ? 'Advance Payment' : 'On-account Payment'}</span>
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Amount <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={advanceAmount || ''}
                    onChange={(e) => { setAdvanceAmount(parseFloat(e.target.value) || 0); if (errors.advanceAmount) setErrors(prev => ({ ...prev, advanceAmount: null })); }}
                    className={`w-full px-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-teal-500 ${errors.advanceAmount ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                    placeholder="0.00"
                    data-testid="advance-amount"
                  />
                  {errors.advanceAmount && <p className="text-xs text-red-600 mt-0.5">{errors.advanceAmount}</p>}
                </div>
                {formData.paymentType === 'advance' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">For Purchase Order</label>
                    <select
                      value={advancePurchaseOrder}
                      onChange={(e) => setAdvancePurchaseOrder(e.target.value)}
                      disabled={!formData.supplier}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-teal-500"
                      data-testid="advance-po"
                    >
                      <option value="">{formData.supplier ? 'Optional — select a PO' : 'Select a supplier first'}</option>
                      {supplierPOs.map(po => (
                        <option key={po._id} value={po._id}>{po.orderNumber} · {Number(po.totalAmount ?? po.totals?.grandTotal ?? 0).toFixed(2)}</option>
                      ))}
                    </select>
                  </div>
                )}
                <p className={`text-xs text-gray-500 ${formData.paymentType === 'advance' ? '' : 'md:col-span-2'}`}>
                  {formData.paymentType === 'advance'
                    ? 'Paid before the goods / service arrive. On approval: Dr Supplier Advance account (or the supplier ledger when none is set in Transaction Ledger Mapping) · Cr bank / cash. Set it off in the Against GRN payment for the bill.'
                    : 'Paid against the supplier\'s running balance without matching bills. On approval: Dr supplier · Cr bank / cash. Set it off in a later Against GRN payment to match it with GRNs.'}
                </p>
              </div>
            </div>
          )}

          {/* GRN Allocation Section */}
          {formData.paymentType === 'against_grn' && (
            <GRNAllocationGrid
              supplier={formData.supplier}
              grnAllocations={grnAllocations}
              onGRNAllocationsChange={setGRNAllocations}
              errors={errors}
              loading={false}
              onLoadAvailableGRNs={loadAvailableGRNs}
            />
          )}

          {/* SRN Allocation Section — returns are set off through their debit note now; shown only for
              payments that already carry a return set-off (made before debit notes were linked) */}
          {formData.supplier && srnAllocations.length > 0 && (
            <SRNAllocationGrid
              supplier={formData.supplier}
              srnAllocations={srnAllocations}
              onSRNAllocationsChange={setSRNAllocations}
              errors={errors}
              loading={false}
              onLoadAvailableSRNs={loadAvailableSRNs}
            />
          )}

          {/* Advance / on-account set-off */}
          {formData.supplier && !isAdvanceType && (
            <AdvanceAllocationGrid
              supplier={formData.supplier}
              advanceAllocations={advanceAllocations}
              onAdvanceAllocationsChange={setAdvanceAllocations}
              errors={errors}
              loading={false}
              onLoadAvailableAdvances={loadAvailableAdvances}
            />
          )}

          {/* Debit Note Allocation Section */}
          {formData.supplier && !isAdvanceType && (
            <DebitNoteAllocationGrid
              supplier={formData.supplier}
              debitNoteAllocations={debitNoteAllocations}
              onDebitNoteAllocationsChange={setDebitNoteAllocations}
              errors={errors}
              loading={false}
              onLoadAvailableDebitNotes={loadAvailableDebitNotes}
            />
          )}

          {/* Payment Modes then Summary stacked */}
          <div className="flex flex-col gap-3">
            <PaymentModesGrid
              paymentModes={paymentModes}
              onPaymentModesChange={setPaymentModes}
              targetAmount={netPaymentAmount}
              errors={errors}
              disabled={saving}
            />

            <PaymentSummarySection
              grnAllocations={grnAllocations}
              srnAllocations={srnAllocations}
              debitNoteAllocations={debitNoteAllocations}
              advanceAllocations={isAdvanceType ? [] : advanceAllocations}
              advanceAmount={isAdvanceType ? (parseFloat(advanceAmount) || 0) : 0}
              paymentType={formData.paymentType}
              creditNotesApplied={creditNotesApplied}
              onCreditNotesChange={setCreditNotesApplied}
              creditNote={creditNote}
              paymentModes={paymentModes}
              onCreditNoteChange={(c) => { setCreditNote(c); if (errors.creditNote) setErrors(prev => ({ ...prev, creditNote: null })); }}
            />

            {errors.creditNote && <p className="text-xs text-red-600 -mt-2">{errors.creditNote}</p>}
          </div>

          {/* Supporting documents: supplier invoice, bank slip, cheque copy… */}
          <ProcurementDocuments ref={docsRef} docType="supplier-payments" docId={isEdit ? id : null} />

          {/* Notes & Actions Footer */}
          <div className="flex items-end justify-between gap-4 pt-2 border-t border-gray-100">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Additional Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                rows="2"
                placeholder="Enter any additional notes..."
              />
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              {formData.status === 'approved' && (
                <button
                  onClick={() => handleSave('process_payment')}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded hover:from-green-700 hover:to-emerald-700 transition-all disabled:opacity-50"
                  disabled={saving}
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Process Payment
                </button>
              )}
              <button
                onClick={handleSave}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={saving}
              >
                {saving && (
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                )}
                {saving ? 'Saving...' : 'Save for Approval'}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default SupplierPaymentForm;